/* eslint-disable react-hooks/exhaustive-deps */
import { useAppDispatch } from 'ducks/hooks'
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Autocomplete, BottomLink, Box, Button, CircularProgress, Grid, Radio, RadioGroup, SelectAddress, TextFieldLabel, Typography } from 'components/UI'

import { canadaStates, EMAIL_TYPE, getAbbFromNameAndCountry, getSelectedAsOption, getStatesOptionList, getStatesOptionListCanada, history, isEmpty, PHONE_MASK_INPUT, states, USER_TYPE, validateEmail } from 'helpers'

import { canadaFlag, googleMaps, icons, obAgent, obHomeowner, obInstitutionalClient, usaFlag } from 'assets'


import { getConfigValue, getNewEstimateValue, getUser } from 'ducks/selectors'
import { Address } from 'ducks/types'

import { useStepperContext } from 'hooks/useStepperContext'

import { LinearProgress } from '@material-ui/core'
import { Auth } from 'api/index'
import { CreateUserRequest } from 'api/punchlist/users/types'
import FormRadioButton from 'components/UI/CustomUI/atoms/FormRadioButton'
import { configActions, userActions } from 'ducks/actions'
import { UserType } from 'ducks/user/types'
import useStyles from './styles'

const Location: FC = () => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const { showWarnings, saveNewValue, setCanGoForward, setShowWarnings, goBack } = useStepperContext()
  const push = history.usePush()

  const user = useSelector(getUser)
  const estimate = useSelector(getNewEstimateValue('estimate'))
  const address = !isEmpty(user?.address?.line_1) ? user?.address : estimate?.properties?.address
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [street, setStreet] = useState(address?.line_1)
  const [city, setCity] = useState(address?.city)
  const [state, setState] = useState(address?.state)
  const [zipCode, setZipCode] = useState(address?.zipCode)
  const [unitNumber, setUnitNumber] = useState(address?.unitNumber)
  const [latitude, setLatitude] = useState(address?.latitude)
  const [longitude, setLongitude] = useState(address?.longitude)
  const [email, setEmail] = useState(user?.email ? user?.email[0]?.email : '')
  const userInfo = Auth.getUserInfo()
  const userEmailVerified = userInfo?.email_verified


  // const contacts = estimate.properties?.contacts ? estimate.properties.contacts : []
  const mainContact = useSelector(getNewEstimateValue('mainContact'))
  const [phone, setPhone] = useState(user.phone ? user.phone?.toString() : mainContact?.phone ? mainContact?.phone : '')

  const [country, setCountry] = useState(address?.country ? address?.country : "United States")

  const userIsNotHomeowner = ![USER_TYPE.HOME_BUYER, USER_TYPE.HOMEOWNER].includes(user.clientType)

  const [openMap, setOpenMap] = useState(userIsNotHomeowner || false)
  const redirectPath = useSelector(getConfigValue('redirectPath'))
  const savedAddress = {
    line_1: street?.trim(),
    line_2: unitNumber,
    city: city?.trim(),
    state: state?.trim(),
    zipCode: zipCode?.trim(),
    latitude: latitude,
    longitude: longitude,
    country: country
  }
  // const showCompanyName = ["BROKER", "INSTITUTIONAL"].includes(user?.clientType)

  const zipCodeMaxLength = country === "Canada" ? 8 : 6

  const isValidZIPCode = useCallback(() => {
    if (country === 'Canada') {
      return zipCode?.trim().length <= 7
    } else {
      return zipCode?.length === 5
    }
  }, [country, zipCode])

  const validateAddress: boolean = !isEmpty(street) &&
    !isEmpty(city) &&
    !isEmpty(state) &&
    !isEmpty(zipCode) &&
    isValidZIPCode() &&
    !isEmpty(country)

  const validatePhone = !isEmpty(phone) && parseInt(phone?.replaceAll('-', ''))?.toString().length === 10

  const validate = validateAddress &&
    validatePhone


  useEffect(() => {
    saveNewValue('address', {
      line_1: street?.trim(),
      line_2: unitNumber,
      city: city?.trim(),
      state: state?.trim(),
      zipCode: zipCode?.trim(),
      latitude: latitude,
      longitude: longitude,
      country: country
    })
  }, [city, state, street, zipCode, latitude, longitude, country, unitNumber])


  useEffect(() => {
    setCanGoForward(validate)
  }, [validate])

  useEffect(() => { setShowWarnings(false) }, [])

  const handleChange = (newAddress: Address) => {
    setStreet(newAddress.line_1)
    setCity(newAddress.city)
    setState(!isEmpty(getAbbFromNameAndCountry(newAddress.state, newAddress.country)) ? newAddress.state : '')
    setZipCode(newAddress.zipCode)
    setLatitude(newAddress.latitude)
    setLongitude(newAddress.longitude)
    setCountry(newAddress.country)
    setUnitNumber(newAddress.line_2)
  }

  const handleChangeCountry = (e: ChangeEvent<HTMLInputElement>) => {
    if (country !== e.target.value) {
      setCountry(e.target.value)
      setState(null)
    }
  }

  const updateRegisterImg = (value: USER_TYPE) => {
    switch (value) {
      case USER_TYPE.BROKER:
        dispatch(configActions.setConfigValue({ type: 'registerImg', value: obAgent }))

        break
      case USER_TYPE.HOMEOWNER:
        dispatch(configActions.setConfigValue({ type: 'registerImg', value: obHomeowner }))

        break
      case USER_TYPE.INSTITUTIONAL:
        dispatch(configActions.setConfigValue({ type: 'registerImg', value: obInstitutionalClient }))

        break
      default:
        break
    }
  }

  const updateUser = () => {
    if (isSubmitting) return
    setShowWarnings(true)
    if (validate) {
      setIsSubmitting(true)
      dispatch(configActions.setConfigValue({ type: 'registrationLoading', value: true }))
      saveNewValue('email', [{ emailType: '', email: email }])
      saveNewValue(
        'phone',
        phone
      )

      const request: CreateUserRequest = {
        email: [{ emailType: EMAIL_TYPE.PRIMARY, email: email }],
        address: {
          line_1: street?.trim(),
          line_2: unitNumber,
          city: city?.trim(),
          state: state?.trim(),
          zipCode: zipCode?.trim(),
          latitude: latitude,
          longitude: longitude,
          country: country
        } as Address,
        phone: parseInt(phone.replaceAll('-', '')),
        firstName: user?.firstName,
        lastName: user?.lastName,
        clientType: user?.clientType,
      }

      dispatch(configActions.setConfigValue({ type: 'showReferralModal', value: true }))

      if (!isEmpty(user?.companyName)) request.companyName = user?.companyName?.toLowerCase()

      if (userEmailVerified) {
        const requestNew: Partial<UserType> = {
          address: {
            line_1: street?.trim(),
            line_2: unitNumber,
            city: city?.trim(),
            state: state?.trim(),
            zipCode: zipCode?.trim(),
            latitude: latitude,
            longitude: longitude,
            country: country
          } as Address,
          phone: parseInt(phone.replaceAll('-', '')),
          clientType: user?.clientType,
          firstName: user?.firstName,
          lastName: user?.lastName,
          registrationComplete: true,
        }
        if (!isEmpty(user?.companyName)) requestNew.companyName = user?.companyName?.toLowerCase()
        dispatch(userActions.updateUser(requestNew, (succ) => {
          if (succ) {
            dispatch(userActions.setUserValues({ attrs: { email: [{ emailType: EMAIL_TYPE.PRIMARY, email: email }] } }))
            push('dashboard')
          }
          dispatch(configActions.setConfigValue({ type: 'registrationLoading', value: false }))
          setIsSubmitting(false)
        }))
      } else {

        dispatch(userActions.newCreateUser(request, (succ) => {
          if (succ) {
            dispatch(userActions.setUserValues({ attrs: { email: [{ emailType: EMAIL_TYPE.PRIMARY, email: email }] } }))
            push('register-success')
          }
          dispatch(configActions.setConfigValue({ type: 'registrationLoading', value: false }))
          setIsSubmitting(false)
        }))
      }
    }
  }


  return (
    <Box className={classes.root}>
      <Grid item>
        <Typography variant="h5" className={classes.title}>What is your contact information?</Typography>
      </Grid>

      <Grid>
        <LinearProgress variant="determinate" value={70} />
        {/* <Typography className={classes.subtitle}>Your role</Typography> */}
      </Grid>
      {/*  {userIsNotHomeowner &&
        <Grid>
          <Typography className={classes.brokerTitle}>
            Please enter your office location
          </Typography>
        </Grid>} */}
      {/* {showCompanyName && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextFieldLabel
              label='Company Name:'
              type='text'
              id='company-name'
              value={companyName}
              autoComplete='off'
              error={showWarnings && isEmpty(companyName)}
              onChange={(ev) => {
                saveNewValue('companyName', ev.target.value)
                setCompanyName(ev.target.value)
              }}
              placeholder='Company Name'
            />
          </Grid>
        </Grid>
      )} */}
      <Grid item className={`${classes.middleButtonItem} ${openMap ? classes.middleButtonItemOpen : ''}`}>
        <Button
          onClick={() => setOpenMap(!openMap)}
          variant='outlined'
          className={classes.googleMapsButton}
          endIcon={<img src={googleMaps} alt='' className={classes.iconMap} />}
        >
          {openMap ? 'Enter Address Manually' : 'Use Google Map'}
        </Button>
      </Grid>
      <Grid container spacing={2} /* className={`${classes.grid} ${openMap ? classes.gridOpen : ''}`} */>
        <Grid item className={classes.middleInputItem}>
          {openMap
            ? (
              <SelectAddress
                showVertical
                title={
                  <Typography variant='body1' className={classes.title}>
                    Address:
                  </Typography>
                }
                showUnit={true}
                onChange={handleChange}
                savedAddress={savedAddress}
                showWarnings={showWarnings && !validateAddress}
                customClass={`${classes.modalContent} ${openMap ? classes.modalContentOpen : ''}`}
                showMap={false}
              />)
            : (
              <>
                <Grid style={{ paddingBottom: 20 }}>
                  <Typography style={{ paddingBottom: 8 }} className={classes.inputAutocomplete}> Country: </Typography>
                  <RadioGroup
                    value={country}
                    onChange={handleChangeCountry}
                    className={classes.radioContainer}
                  >
                    <FormRadioButton
                      style={country === "United States" ? { borderColor: "var(--bosscat-blue-600)" } : {}}
                      value={'United States'}
                      control={<Radio color='primary' />}
                      label={
                        <>
                          <img src={usaFlag} alt='' className={classes.flags} />
                          <Typography variant='caption'>USA</Typography>
                        </>
                      }
                      className={classes.radio}
                    />
                    <FormRadioButton
                      style={country === "Canada" ? { borderColor: "var(--bosscat-blue-600)" } : {}}
                      value={'Canada'}
                      control={<Radio color='primary' />}
                      label={
                        <>
                          <img src={canadaFlag} alt='' className={classes.flags} />
                          <Typography variant='caption'>Canada</Typography>
                        </>
                      }
                      className={classes.radio}
                    />
                  </RadioGroup>
                </Grid>

                <TextFieldLabel
                  label='Your Address:'
                  onChange={ev => { setStreet(ev.target.value) }}
                  error={showWarnings && isEmpty(street?.trim())}
                  value={street}
                  placeholder='Enter your address'
                />
              </>
            )}
        </Grid>
      </Grid>

      {!openMap && (
        <Grid container spacing={2}>
          <Grid item container spacing={2}>
            <Grid item xs={6}>
              <TextFieldLabel
                type={undefined}
                label='Unit Number'
                // error={showWarnings && (isEmpty(unitNumber) || !isValidZIPCode())}
                onChange={ev => {
                  setUnitNumber(ev.target.value)
                }}
                value={unitNumber}
                placeholder='Apt. 101'
              />
            </Grid>
            <Grid item xs={6}>
              <TextFieldLabel
                type={country !== 'Canada' ? 'number' : undefined}
                label='ZIP/Postal'
                error={showWarnings && (isEmpty(zipCode) || !isValidZIPCode())}
                onChange={ev => {
                  setZipCode(ev.target.value.length < zipCodeMaxLength ? ev.target.value : zipCode)
                }}
                value={zipCode}
                placeholder='Enter your ZIP code'
              />
            </Grid>
          </Grid>
          <Grid item container spacing={2}>
            <Grid xs={6} item className={classes.bottomInputItems}>
              <Autocomplete
                label='State'
                onChange={(value: any) => setState(value.key)}
                value={!isEmpty(states) && !isEmpty(state)
                  ? getSelectedAsOption(states.find(opt => opt?.abbreviation === state) || canadaStates.find(opt => opt?.abbreviation === state))
                  : null}
                error={showWarnings && !state}
                options={country === 'Canada' ? getStatesOptionListCanada() : getStatesOptionList()}
                placeholder='Select your state'
                labelClassName={classes.inputAutocomplete}
              />
            </Grid>
            <Grid xs={6} item className={classes.bottomInputItems}>
              <TextFieldLabel
                label='City'
                onChange={ev => { setCity(ev.target.value) }}
                error={showWarnings && isEmpty(city?.trim())}
                value={city}
                placeholder='Enter your city'
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid container item style={{ marginBottom: "1rem" }}>
        <TextFieldLabel
          label="Your Email"
          onChange={(ev) => {
            setEmail(ev.target.value)
          }}
          value={email}
          error={
            showWarnings &&
            !validateEmail(email)
          }
          placeholder="valid@email.com"
          className={classes.phoneInput}
        />
      </Grid>
      <Grid container item style={{ marginBottom: "1rem" }}>
        <TextFieldLabel
          label="Your Mobile Phone Number"
          onChange={(ev) => {
            setPhone(ev.target.value)
          }}
          mask={PHONE_MASK_INPUT}
          value={phone}
          error={
            showWarnings &&
            !validatePhone
          }
          placeholder="123-123-1234"
          className={classes.phoneInput}
        />
      </Grid>
      <BottomLink
        question='Already have an account?'
        linkText='Sign in here'
        onClick={() => {
          push('login')
        }}
      />
      <Grid container item xs={12} style={{ display: 'flex', marginTop: '1rem', width: '100%' }} justifyContent='space-between'>
        <Button
          type='submit'
          variant='outlined'
          size='small'
          className={classes.buttonBack}
          startIcon={<icons.ArrowBack />}
          onClick={goBack}
        >
          Back
        </Button>
        <Button
          type='submit'
          variant='contained'
          size='small'
          className={classes.buttonNext}
          endIcon={
            isSubmitting
              ? <CircularProgress size={16} color="inherit" />
              : <icons.ArrowForward />
          }
          // disabled={!validate}
          onClick={updateUser}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Loading…' : 'Next'}
        </Button>
      </Grid>
    </Box>
  )
}

export default Location
